import React from "react";
import { ArrowUpRight, UserRound } from "lucide-react";
import { LINKEDIN_URL } from "../data/mock";

const HeroSection = () => {
  const openLinkedIn = () => {
    window.open(LINKEDIN_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <section className="relative w-full hero-bg overflow-hidden">
      {/* Soft warm wash on the left edge */}
      <div
        className="absolute inset-y-0 left-0 w-[55%] pointer-events-none"
        style={{
          background:
            "linear-gradient(135deg, rgba(255,232,205,0.55) 0%, rgba(255,247,237,0.25) 45%, rgba(255,255,255,0) 80%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-14 lg:pt-20 pb-10 lg:pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-6 items-center">
          {/* Left content */}
          <div className="relative z-20 lg:col-span-6">
            <div className="fade-up">
              <span className="inline-block text-[12px] font-semibold tracking-[0.22em] text-[#FF7A1A] uppercase mb-5">
                Careers at Learn Overseas
              </span>
              <h1 className="font-display text-[42px] sm:text-[52px] lg:text-[68px] leading-[1.02] font-extrabold text-slate-900">
                Build Your Career.
                <br />
                Shape{" "}
                <span className="text-[#FF7A1A]">Global Futures.</span>
              </h1>
              <p className="mt-6 text-slate-500 text-[17px] leading-relaxed max-w-xl">
                At Learn Overseas, we don’t just help students go abroad—we help
                them build a better future. Join a passionate team that’s
                transforming lives every day.
              </p>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-4 fade-up delay-2">
              <button
                onClick={openLinkedIn}
                className="btn-primary inline-flex items-center gap-2 rounded-xl px-6 py-4 text-[15px] font-semibold"
              >
                View Open Positions
                <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
              </button>

              <button
                onClick={openLinkedIn}
                className="btn-ghost inline-flex items-center gap-2 rounded-xl px-6 py-4 text-[15px] font-semibold"
              >
                Life at Learn Overseas
                <UserRound className="w-4 h-4" strokeWidth={2.2} />
              </button>
            </div>
          </div>

          {/* Right illustration */}
          <div className="relative lg:col-span-6 z-10">
            <HeroIllustration />
          </div>
        </div>
      </div>
    </section>
  );
};

const HeroIllustration = () => {
  return (
    <div className="relative w-full aspect-[6/5] sm:aspect-[6/5] lg:aspect-[5/4]">
      {/* World map dots faded */}
      <div
        className="absolute inset-0 opacity-50 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(15, 23, 42, 0.08) 1px, transparent 0)",
          backgroundSize: "16px 16px",
          maskImage:
            "radial-gradient(ellipse 70% 70% at 50% 50%, black 35%, transparent 75%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 70% 70% at 50% 50%, black 35%, transparent 75%)",
        }}
      />

      {/* Faint world map SVG */}
      <svg
        className="absolute inset-0 w-full h-full opacity-25"
        viewBox="0 0 600 500"
        fill="none"
        aria-hidden
      >
        <g stroke="#F5B27A" strokeWidth="0.8" fill="none">
          {/* simplified continents hint - just curved lines */}
          <path d="M40 200 Q 120 160 200 200 T 360 210 T 560 200" />
          <path d="M60 260 Q 160 230 280 270 T 540 260" />
          <path d="M80 320 Q 200 290 340 320 T 540 310" />
        </g>
      </svg>

      {/* Dashed arc - behind people */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox="0 0 600 500"
        fill="none"
        aria-hidden
      >
        <path
          d="M30 430 C 110 200, 320 110, 500 70"
          stroke="#FFB37A"
          strokeWidth="1.6"
          strokeDasharray="5 7"
          fill="none"
          opacity="0.85"
        />
      </svg>

      {/* Eiffel Tower - behind people, slightly left */}
      <svg
        className="absolute bottom-0 left-[8%] w-[28%] h-[88%] opacity-95"
        viewBox="0 0 120 320"
        fill="none"
        aria-hidden
      >
        <g stroke="#2DBE9F" strokeWidth="1.6" fill="none" strokeLinejoin="round">
          {/* base */}
          <path d="M10 310 L60 30 L110 310" />
          <path d="M22 260 L98 260" />
          <path d="M30 220 L90 220" />
          <path d="M38 175 L82 175" />
          <path d="M44 130 L76 130" />
          <path d="M50 90 L70 90" />
          <path d="M60 30 L60 8" />
          {/* arches at base */}
          <path d="M25 280 Q 60 240 95 280" />
          {/* lattice diagonals */}
          <path d="M22 260 L38 175" />
          <path d="M98 260 L82 175" />
          <path d="M30 220 L44 130" />
          <path d="M90 220 L76 130" />
        </g>
      </svg>

      {/* Big Ben - behind people, right */}
      <svg
        className="absolute bottom-0 right-[-2%] w-[24%] h-[95%] opacity-95"
        viewBox="0 0 120 360"
        fill="none"
        aria-hidden
      >
        <g stroke="#9FB7CF" strokeWidth="1.6" fill="none" strokeLinejoin="round">
          {/* tower body */}
          <rect x="35" y="180" width="50" height="170" />
          {/* clock section */}
          <rect x="32" y="150" width="56" height="34" />
          <circle cx="60" cy="167" r="9" />
          <line x1="60" y1="167" x2="60" y2="161" />
          <line x1="60" y1="167" x2="64" y2="167" />
          {/* small upper section */}
          <rect x="38" y="120" width="44" height="30" />
          {/* spire */}
          <path d="M30 120 L60 70 L90 120" />
          <path d="M60 70 L60 40" />
          <path d="M55 40 L65 40" />
          <path d="M58 30 L62 30" />
          {/* base windows */}
          <line x1="45" y1="220" x2="75" y2="220" />
          <line x1="45" y1="250" x2="75" y2="250" />
          <line x1="45" y1="280" x2="75" y2="280" />
          <line x1="45" y1="310" x2="75" y2="310" />
        </g>
      </svg>

      {/* Small bell tower behind, left of big ben */}
      <svg
        className="absolute bottom-0 right-[22%] w-[10%] h-[55%] opacity-60"
        viewBox="0 0 80 220"
        fill="none"
        aria-hidden
      >
        <g stroke="#C9D6E2" strokeWidth="1.4" fill="none">
          <rect x="20" y="100" width="40" height="115" />
          <path d="M15 100 L40 70 L65 100" />
          <line x1="30" y1="130" x2="50" y2="130" />
          <line x1="30" y1="160" x2="50" y2="160" />
          <line x1="30" y1="190" x2="50" y2="190" />
        </g>
      </svg>

      {/* Plane top-right */}
      <div className="absolute top-[6%] right-[2%] plane-float z-[3]">
        <svg width="58" height="58" viewBox="0 0 64 64" fill="none">
          <path
            d="M58 8 L34 30 L18 26 L12 32 L28 38 L26 44 L34 46 L36 52 L42 38 L58 8 Z"
            fill="#FF7A1A"
          />
        </svg>
      </div>

      {/* The people image - on top of landmarks */}
      <img
        src="/hero_team_transparent.png"
        alt="Learn Overseas team"
        className="absolute inset-0 m-auto w-[100%] h-[100%] object-contain object-bottom z-[2] select-none"
        draggable={false}
      />
    </div>
  );
};

export default HeroSection;
